import { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, CredentialResponse, useGoogleOneTapLogin } from '@react-oauth/google';
import { useAuthStore } from '@/stores/authStore';

const LoginPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const { login, loginWithGoogle, isAuthenticated, isLoading, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    console.log('✅ Google OAuth success, credential received');
    console.log('Credential length:', credentialResponse.credential?.length);

    if (credentialResponse.credential) {
      try {
        console.log('📤 Sending credential to backend...');
        await loginWithGoogle(credentialResponse.credential);
        console.log('✅ Login successful!');
        // Navigation will be handled by the useEffect when isAuthenticated changes
      } catch (err: any) {
        console.error('❌ Google login error:', err);
        console.error('Error response:', err.response?.data);
        console.error('Error status:', err.response?.status);
        console.error('Error message:', err.message);
      }
    } else {
      console.error('❌ No credential in Google response');
    }
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
  };

  // Enable Google One Tap for better UX
  useGoogleOneTapLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    clearError();

    try {
      await login({ email, password });
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleButtonClick = () => {
    // Trigger the hidden Google Login button
    const googleButton = googleButtonRef.current?.querySelector('div[role="button"]') as HTMLElement;
    if (googleButton) {
      googleButton.click();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F9FAFB',
        padding: 3,
      }}
    >
      {/* Login Card */}
      <Box
        sx={{
          maxWidth: 440,
          width: '100%',
          backgroundColor: 'white',
          borderRadius: 1.5,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          padding: isMobile ? 3 : 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header Section - Structured */}
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Box
              component="img"
              src="/logoclearblack.png"
              alt="Logo"
              sx={{
                height: 56,
                width: 'auto',
              }}
            />
          </Box>

          {/* Brand Text */}
          <Typography
            sx={{
              fontSize: '15px',
              textTransform: 'uppercase',
              letterSpacing: '1.2px',
              fontWeight: 700,
              color: theme.palette.primary.main,
              lineHeight: 1.4,
              mb: 1,
            }}
          >
            Business Intelligence
          </Typography>
          <Typography
            sx={{
              fontSize: '22px',
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.3,
              mb: 2.5,
            }}
          >
            Dashboard
          </Typography>

          {/* Divider */}
          <Box
            sx={{
              width: '60px',
              height: '2px',
              backgroundColor: theme.palette.primary.main,
              margin: '0 auto',
              mb: 2.5,
              borderRadius: '2px',
            }}
          />

          {/* Subtitle */}
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              color: theme.palette.text.secondary,
              lineHeight: 1.5,
            }}
          >
            Sign in to access your business intelligence dashboard
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {/* Google Sign In - Main Login Method */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={isSubmitting}
          startIcon={<GoogleIcon sx={{ fontSize: '20px' }} />}
          sx={{
            py: 1.25,
            mb: 3,
            backgroundColor: '#FFFFFF',
            color: '#374151',
            border: '1px solid #D1D5DB',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            textTransform: 'none',
            fontSize: '15px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#F9FAFB',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            },
            '&:active': {
              backgroundColor: '#F3F4F6',
            },
            '& .MuiButton-startIcon': {
              marginRight: '12px',
            }
          }}
          onClick={handleGoogleButtonClick}
        >
          Sign in with Google
        </Button>

        {/* Hidden GoogleLogin for OAuth functionality */}
        <Box ref={googleButtonRef} sx={{ opacity: 0, height: 0, overflow: 'hidden' }}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap={false}
          />
        </Box>

        {/* Divider */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, position: 'relative' }}>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: '#D1D5DB' }} />
          <Typography
            variant="body2"
            sx={{
              px: 2,
              backgroundColor: 'white',
              color: '#6B7280',
              fontSize: '0.875rem',
            }}
          >
            or
          </Typography>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: '#D1D5DB' }} />
        </Box>

        {/* Email/Password Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitting}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.375rem',
                '& fieldset': {
                  borderColor: '#D1D5DB',
                },
              },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isSubmitting}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.375rem',
                '& fieldset': {
                  borderColor: '#D1D5DB',
                },
              },
            }}
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting || isLoading}
            sx={{
              py: 1.25,
              borderRadius: '0.375rem',
              backgroundColor: 'primary.main',
              color: '#FFFFFF',
              textTransform: 'none',
              fontSize: '15px',
              fontWeight: 500,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#3B9AA3',
                boxShadow: 'none',
              },
            }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
          </Button>
        </Box>
      </Box>

      {/* Footer Text */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography
          variant="body2"
          sx={{
            color: '#6B7280',
            fontSize: '0.875rem',
            mb: 1,
          }}
        >
          © 2025 IMDM Articles - Business Intelligence Platform
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
